// Social platform definitions for URL validation
const SOCIAL_PLATFORMS = {
  linkedin: {
    baseUrl: "https://www.linkedin.com",
    domains: ["linkedin.com", "www.linkedin.com"],
  },
  twitter: {
    baseUrl: "https://twitter.com",
    domains: ["twitter.com", "www.twitter.com", "x.com", "www.x.com"],
  },
  facebook: {
    baseUrl: "https://www.facebook.com",
    domains: ["facebook.com", "www.facebook.com"],
  },
  instagram: {
    baseUrl: "https://www.instagram.com",
    domains: ["instagram.com", "www.instagram.com"],
  },
  youtube: {
    baseUrl: "https://www.youtube.com",
    domains: ["youtube.com", "www.youtube.com"],
  },
  github: {
    baseUrl: "https://github.com",
    domains: ["github.com", "www.github.com"],
  },
  website: {
    baseUrl: "",
    domains: [], // Allow any domain for website type
  },
  email: {
    baseUrl: "",
    domains: [], // Allow any domain for email type
  },
};

/**
 * Validates if a URL matches the expected platform
 * @param {string} url - The URL to validate
 * @param {string} platform - The platform type (linkedin, twitter, etc.)
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateUrl = (url, platform) => {
  if (!url) return false;

  // If platform is email, validate email format
  if (platform === "email") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url) && url.includes("@");
  }

  // Allow any valid URL for website type
  if (platform === "website") {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // Validate social platform URLs
  try {
    const urlObj = new URL(url);
    const platformInfo = SOCIAL_PLATFORMS[platform];

    if (!platformInfo) {
      return false;
    }

    // For website type, allow any domain
    if (platformInfo.domains.length === 0) {
      return true;
    }

    // Check if the URL domain matches any of the allowed domains for this platform
    const isValidDomain = platformInfo.domains.some((domain) => {
      const normalizeHostname = (hostname) => hostname.replace(/^www\./, "");
      return normalizeHostname(urlObj.hostname) === normalizeHostname(domain);
    });

    if (!isValidDomain) {
      return false;
    }

    // Ensure HTTPS for security (optional, can be removed if HTTP is allowed)
    if (urlObj.protocol !== "https:") {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Get platform-specific placeholder text
 * @param {string} platform - The platform type
 * @returns {string} - Placeholder text for the URL input
 */
export const getUrlPlaceholder = (platform) => {
  const placeholders = {
    linkedin: "https://linkedin.com/in/yourprofile",
    twitter: "https://twitter.com/yourusername",
    facebook: "https://facebook.com/yourprofile",
    instagram: "https://instagram.com/yourusername",
    youtube: "https://youtube.com/c/yourchannel",
    github: "https://github.com/yourusername",
    website: "https://yourwebsite.com",
  };

  return placeholders[platform] || "https://example.com";
};
